"use client";

import React from "react";
import { InfoText } from "@/components/InfoText";
import { Info, X } from "lucide-react";
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import Checkmark from "@/components/icons/Checkmark";
import { OfferResponse } from "@/types/simpego-api";
import { TableRow, Translate } from "@/utils/coverageRows";

/** One value cell of a coverage row — shared by the comparison table and the cart/confirmation summary,
 * so both read exactly the same. `loading` only affects the placeholder of a metric without a value yet. */
export const RowValue = ({
  row,
  offer,
  t,
  loading,
  alignLeft = false,
  iconEnd = false,
}: {
  row: Exclude<TableRow, { kind: "section" }>;
  offer: OfferResponse | null;
  t: Translate;
  loading: boolean;
  /** Left-aligns the value (comparison table) so check marks, crosses and figures of a column start at the same edge. */
  alignLeft?: boolean;
  /** Puts the check mark / cross after the amount (right edge), so a right-aligned column of marks lines up. */
  iconEnd?: boolean;
}) => {
  if (row.kind === "metric") {
    const value = row.value(offer, t);
    return (
      <span className={cn("flex flex-col leading-tight", alignLeft ? "items-start" : "items-center")}>
        <span className="text-lg font-bold">{value?.main ?? (loading ? "…" : "–")}</span>
        {value?.sub && <span className="text-sm text-muted-foreground">{value.sub}</span>}
      </span>
    );
  }
  const coverage = row.value(offer, t);
  return coverage.isCovered ? (
    <span className={cn("inline-flex items-center gap-1.5 text-lg", iconEnd && "flex-row-reverse")}>
      {!(coverage.hideCheckmark && coverage.detail) && <Checkmark role="img" aria-label={t("a11y.included")} className="h-4 w-4 shrink-0 text-primary/70" />}
      {coverage.detail && <span className={cn("text-muted-foreground", alignLeft && "text-left")}>{coverage.detail}</span>}
    </span>
  ) : (
    <span className={cn("inline-flex items-center gap-1.5 text-lg text-muted-foreground", iconEnd && "flex-row-reverse")}>
      <X role="img" aria-label={t("a11y.notIncluded")} className="h-4 w-4 shrink-0 opacity-40" />
      {coverage.detail && <span className={cn(alignLeft && "text-left")}>{coverage.detail}</span>}
    </span>
  );
};

/** Info icon behind a row label; opens a dialog with the explanation. */
export const LabelInfo = ({ labelKey, infoKey, infoTitleKey, t }: { labelKey: string; infoKey?: string; infoTitleKey?: string; t: Translate }) => (
  <Dialog>
    <DialogTrigger asChild>
      <button
        type="button"
        onClick={(e) => e.stopPropagation()}
        className="ml-1.5 inline-flex shrink-0 rounded-full align-middle text-muted-foreground hover:text-primary focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
        aria-label={`${t("common.info")}: ${t(labelKey)}`}
      >
        <Info className="h-4 w-4" />
      </button>
    </DialogTrigger>
    <DialogContent>
      <DialogHeader>
        <DialogTitle>{t(infoTitleKey ?? labelKey)}</DialogTitle>
        <InfoText text={infoKey ? t(infoKey) : t("priceCustomization.table.infoPlaceholder", { label: t(labelKey) })} />
      </DialogHeader>
    </DialogContent>
  </Dialog>
);

/** Label + info icon. The last word stays together with the icon, so the icon never wraps onto a line of its own. */
export const RowLabel = ({ row, t }: { row: Exclude<TableRow, { kind: "section" }>; t: Translate }) => {
  const words = t(row.labelKey).split(" ");
  const tail = words[words.length - 1];
  return (
    <>
      {words.slice(0, -1).join(" ")}{words.length > 1 && " "}
      <span className="whitespace-nowrap">
        {tail}
        <LabelInfo labelKey={row.labelKey} infoKey={row.infoKey} infoTitleKey={row.infoTitleKey} t={t} />
      </span>
    </>
  );
};

/** Info icon behind a section heading (only when the section has an explanation). */
export const SectionInfo = ({ row, t }: { row: Extract<TableRow, { kind: "section" }>; t: Translate }) =>
  row.infoKey ? (
    <Dialog>
      <DialogTrigger asChild>
        <button type="button" onClick={(e) => e.stopPropagation()} aria-label={t("common.moreInfo")}>
          <Info className="h-4 w-4 opacity-70" />
        </button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t(row.infoTitleKey ?? row.labelKey)}</DialogTitle>
          <InfoText text={t(row.infoKey)} />
        </DialogHeader>
      </DialogContent>
    </Dialog>
  ) : null;
